import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GroupLayer from "@arcgis/core/layers/GroupLayer.js";
import "@esri/calcite-components/main.css";
import "@esri/calcite-components";
import "@esri/calcite-components/components/calcite-slider";
import "@esri/calcite-components/components/calcite-label";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FeatureLayerNFS } from "../../../../extensions/FeatureLayerNFS";
import { getMapView } from "../../../../utils/arcgisUtils";
import Collapsible from "../../../genericComponents/Collapsible/Collapsible";

import CheckedIcon from "../../../../assets/layerlist/checked.svg?react";
import EmptyStarIcon from "../../../../assets/layerlist/emptyStar.svg?react";
import StarIcon from "../../../../assets/layerlist/star.svg?react";
import UncheckedIcon from "../../../../assets/layerlist/unchecked.svg?react";

import { useDispatch, useSelector } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { StoreState } from "../../../../store/rootReducer";
import {
    getFavorites,
    isLayerEnabledOrInMap,
    isLayerFavorited,
    isNFSLayer,
    removeLayerFromFavorites,
    storeLayerInFavorites,
    tryToggleLayer,
} from "../../../../utils/layerlistUtils";
import Checkbox from "../../../genericComponents/Checkbox/Checkbox";
import LayerlistCollapsible from "../LayerlistCollapsible";
import "./LayerlistDetails.css";
import { setShowRemovedFeatures } from "../../../../store/appSlice";
import AerialImagery from "../../AerialImagery/AerialImagery";
import type Layer from "@arcgis/core/layers/Layer";
import type { ListItemModifierEvent } from "@arcgis/core/widgets/LayerList/types";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type WebMap from "@arcgis/core/WebMap";

interface LayerlistDetailsProps {
    configWebMap: WebMap | null | undefined;
    refreshConfigWebMap: () => void;
    onSettingsChanged?: () => void;
    nfsStatusFilterEnabled: boolean;
}

const LayerlistDetails: React.FC<LayerlistDetailsProps> = ({
    configWebMap,
    refreshConfigWebMap,
    onSettingsChanged,
    nfsStatusFilterEnabled,
}) => {
    const dispatch = useDispatch();
    const [favorites, setFavorites] = useState<{ title: string; url: string }[]>(getFavorites());
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);
    const onSettingsChangedRef = useRef(onSettingsChanged);
    const layerListVisibilityWatchHandles = useRef<ResourceHandle[]>([]);
    const watchedLayerListItemUids = useRef(new Set<string>());

    const [nfsLayersList, setNfsLayersList] = useState<React.JSX.Element[]>([]);
    const [favoriteLayers, setFavoriteLayers] = useState<React.JSX.Element[]>([]);
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    // psudo event to trigger a refresh of the layerlist to keep state updated
    // @ts-ignore
    const refreshLayerList = useSelector((state: StoreState) => state.triggers.triggerLayerlistRefresh);

    useEffect(() => {
        onSettingsChangedRef.current = onSettingsChanged;
    }, [onSettingsChanged]);

    const layerListRenderFunction = useCallback((event: ListItemModifierEvent) => {
        const { item } = event;

        const itemUid = item.uid ?? `${item.layer?.id ?? item.title ?? "layer-list-item"}`;
        if (!watchedLayerListItemUids.current.has(itemUid)) {
            watchedLayerListItemUids.current.add(itemUid);
            const visibilityWatchHandle = item.watch("visible", () => {
                onSettingsChangedRef.current?.();
            });
            layerListVisibilityWatchHandles.current.push(visibilityWatchHandle);
        }

        const container = document.createElement("div");
        if (item.layer?.hasOwnProperty("opacity")) {
            const label = document.createElement("calcite-label");

            label.innerHTML += ` <span style="display: flex; flex-direction: row;"><calcite-icon icon="transparency" scale="s" style="vertical-align: middle; margin-right: 0.3rem"></calcite-icon><span>Gjennomsiktighet</span></span>`;
            label.scale = "s";

            const slider = document.createElement("calcite-slider");
            slider.scale = "m";
            slider.labelHandles = true;
            slider.labelTicks = true;
            slider.min = 0;
            slider.minLabel = "0";
            slider.max = 1;
            slider.maxLabel = "1";
            slider.step = 0.01;
            slider.value = item.layer?.opacity ?? 1;
            slider.ticks = 0.25;

            slider.addEventListener("calciteSliderInput", () => {
                if (!item.layer) return;
                let value = -1;
                if (Array.isArray(slider.value)) {
                    value = slider.value[0];
                } else {
                    value = slider.value ?? 1;
                }
                item.layer.opacity = value;
            });

            // Save settings when slider is released
            slider.addEventListener("calciteSliderChange", () => {
                onSettingsChangedRef.current?.();
            });

            label.appendChild(slider);
            container.appendChild(label);
        }

        if (item.layer?.hasOwnProperty("minScale")) {
            const zoomSliderLabel = document.createElement("calcite-label");
            zoomSliderLabel.innerHTML += ` <span style="display: flex; flex-direction: row; margin-top: 1rem;"><calcite-icon icon="magnifying-glass" scale="s" style="vertical-align: middle; margin-right: 0.3rem"></calcite-icon><span>Zoomnivå</span></span>`;
            zoomSliderLabel.scale = "s";

            const zoomSlider = document.createElement("calcite-slider");
            zoomSlider.scale = "m";
            zoomSlider.labelHandles = true;
            zoomSlider.labelTicks = true;
            zoomSlider.min = 0;
            zoomSlider.minLabel = "0";
            zoomSlider.max = 1000000;
            zoomSlider.maxLabel = "1 000 000";
            zoomSlider.step = 1000;
            zoomSlider.value = (item.layer as FeatureLayer)?.minScale ?? 0;
            zoomSlider.ticks = 250000;
            zoomSlider.addEventListener("calciteSliderInput", () => {
                if (!item.layer) return;
                let value = -1;
                if (Array.isArray(zoomSlider.value)) {
                    value = zoomSlider.value[0];
                } else {
                    value = zoomSlider.value ?? 0;
                }
                (item.layer as FeatureLayer).minScale = value;
            });

            // Save settings when slider is released
            zoomSlider.addEventListener("calciteSliderChange", () => {
                onSettingsChangedRef.current?.();
            });

            zoomSliderLabel.appendChild(zoomSlider);
            container.appendChild(zoomSliderLabel);
        }

        item.panel = {
            content: container,
            title: "Change layer opacity",
            icon: "sliders-horizontal",
        };
    }, []);

    const layerListCallbackRef = useCallback(
        (element: any) => {
            if (element) {
                element.componentOnReady().then(() => {
                    element.listItemCreatedFunction = layerListRenderFunction;
                });
            }
        },
        [layerListRenderFunction]
    );

    useEffect(() => {
        const updateFavorites = () => {
            setFavorites(getFavorites());
        };

        globalThis.addEventListener("favoritesChanged", updateFavorites);

        return () => {
            globalThis.removeEventListener("favoritesChanged", updateFavorites);
        };
    }, []);

    useEffect(() => {
        return () => {
            layerListVisibilityWatchHandles.current.forEach((handle) => handle.remove());
            layerListVisibilityWatchHandles.current = [];
            watchedLayerListItemUids.current.clear();
        };
    }, []);

    const addLayerToFavorites = (layer: Layer, add: boolean) => {
        if (add) {
            storeLayerInFavorites(layer);
        } else {
            removeLayerFromFavorites(layer);
        }
        setFavorites(getFavorites());
    };

    const renderNFSLayers = () => {
        const nfsLayers: React.JSX.Element[] = [];
        const mapview = getMapView();
        if (mapview?.map) {
            const layers = mapview.map.layers.toArray();
            for (const layer of layers) {
                if (isNFSLayer(layer)) {
                    nfsLayers.push(
                        <div key={layer.id} className="layerlist-details-layer">
                            <Checkbox
                                checked={layer.visible}
                                checkedIcon={CheckedIcon}
                                uncheckedIcon={UncheckedIcon}
                                id={layer.id}
                                onChange={(checked: boolean) => {
                                    layer.visible = checked;
                                    // Auto-save settings after NFS layer visibility change
                                    onSettingsChanged?.();
                                }}
                            />
                            <label className="w-100" htmlFor={layer.id}>
                                {layer.title}
                            </label>
                            <Checkbox
                                checked={isLayerFavorited(layer)}
                                checkedIcon={StarIcon}
                                uncheckedIcon={EmptyStarIcon}
                                id={layer.id + "-star"}
                                onChange={(checked) => {
                                    addLayerToFavorites(layer, checked);
                                }}
                            />
                        </div>
                    );
                }
            }
        }
        setNfsLayersList(nfsLayers);
    };

    const renderFavoriteLayers = () => {
        const favoriteLayers: React.JSX.Element[] = [];

        const mapview = getMapView();
        if (!mapview?.map || !configWebMap) {
            return favoriteLayers;
        }

        const layers = [];

        const tempFavorites = getFavorites();
        for (const favorite of tempFavorites) {
            let allLayers = mapview.map.allLayers.toArray();
            // Filter out layers that does not have isVersioned property
            allLayers = allLayers.filter((layer) => {
                return (layer as FeatureLayerNFS)?.isVersioned === true;
            });
            allLayers = allLayers.concat(configWebMap?.allLayers.toArray());

            for (const layer of allLayers) {
                if (((layer as any)?.url ?? "") === favorite.url && layer.title === favorite.title) {
                    layers.push(layer);
                }
            }
        }

        for (const layer of layers) {
            favoriteLayers.push(
                <div key={layer.id} className="layerlist-details-layer">
                    <Checkbox
                        checked={isLayerEnabledOrInMap(layer)}
                        checkedIcon={CheckedIcon}
                        uncheckedIcon={UncheckedIcon}
                        id={layer.id}
                        onChange={() => {
                            tryToggleLayer(layer, refreshConfigWebMap, onSettingsChanged);
                        }}
                    />
                    <label className="w-100" htmlFor={layer.id}>
                        {layer.title}
                    </label>
                    <Checkbox
                        checked={isLayerFavorited(layer)}
                        checkedIcon={StarIcon}
                        uncheckedIcon={EmptyStarIcon}
                        id={layer.id + "-star"}
                        onChange={(checked) => {
                            addLayerToFavorites(layer, checked);
                        }}
                    />
                </div>
            );
        }

        setFavoriteLayers(favoriteLayers);
    };

    const renderLayersFromWebMap = () => {
        const renderLayer = (layer: Layer): React.JSX.Element => {
            return (
                <div key={layer.id + refreshLayerList} className="layerlist-details-layer">
                    <Checkbox
                        checked={isLayerEnabledOrInMap(layer)}
                        checkedIcon={CheckedIcon}
                        uncheckedIcon={UncheckedIcon}
                        id={layer.id}
                        onChange={() => {
                            tryToggleLayer(layer, refreshConfigWebMap, onSettingsChanged);
                        }}
                    />
                    <label className="w-100" htmlFor={layer.id}>
                        <span className="layerlist-details-layer-title">{layer.title}</span>
                    </label>
                    <Checkbox
                        checked={isLayerFavorited(layer)}
                        checkedIcon={StarIcon}
                        uncheckedIcon={EmptyStarIcon}
                        id={layer.id + "-star"}
                        onChange={(checked) => {
                            addLayerToFavorites(layer, checked);
                        }}
                    />
                </div>
            );
        };

        const renderGroupLayer = (groupLayer: GroupLayer, topLayer: boolean): React.JSX.Element => {
            const childLayers: React.JSX.Element[] = [];

            for (const childLayer of groupLayer.layers) {
                if (childLayer.type === "group") {
                    childLayers.push(renderGroupLayer(childLayer as GroupLayer, false));
                } else {
                    childLayers.push(renderLayer(childLayer));
                }
            }

            if (topLayer) {
                return (
                    <Collapsible
                        key={groupLayer.id}
                        isOpen={false}
                        setIsOpen={() => {}}
                        title={groupLayer.title ?? "Kunne ikke laste gruppenavn"}
                        titleClassName="layerlist-details-title"
                        className="layerlist-details-collapsible"
                    >
                        {childLayers}
                    </Collapsible>
                );
            } else {
                return (
                    <LayerlistCollapsible
                        key={groupLayer.id}
                        isOpen={false}
                        setIsOpen={() => {}}
                        title={groupLayer.title ?? "Kunne ikke laste gruppenavn"}
                        isChecked={isLayerEnabledOrInMap(groupLayer)}
                        onCheckboxChange={() => {
                            tryToggleLayer(groupLayer, refreshConfigWebMap, onSettingsChanged);
                        }}
                        onToggle={() => {}}
                        isFavorite={isLayerFavorited(groupLayer)}
                        onFavoriteChange={(checked) => {
                            addLayerToFavorites(groupLayer, checked);
                        }}
                    >
                        {childLayers}
                    </LayerlistCollapsible>
                );
            }
        };

        let layers: React.JSX.Element[] = [];

        if (configWebMap) {
            for (const layer of configWebMap.layers) {
                if (layer.type === "group") {
                    layers.push(renderGroupLayer(layer as GroupLayer, true));
                } else {
                    layers.push(renderLayer(layer));
                }
            }
        }

        return layers;
    };

    useEffect(() => {
        renderNFSLayers();
        renderFavoriteLayers();
    }, [refreshLayerList, favorites]);

    useEffect(() => {
        if (selectedTabIndex === 0) {
            refreshConfigWebMap();
        }
        // refreshConfigWebMap is intentionally omitted to avoid re-fetching on every parent re-render
        // since it is passed as an inline function.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTabIndex]);

    return (
        <div className="layerlist-details-container">
            <Tabs
                className="flex-col overflow-hidden"
                selectedTabClassName="selected"
                selectedIndex={selectedTabIndex}
                onSelect={(index) => setSelectedTabIndex(index)}
            >
                <TabList className="layerlist-tab-list">
                    <Tab className="layerlist-tab-button">Kartlag</Tab>
                    <Tab className="layerlist-tab-button">Kartlagsjustering</Tab>
                    <Tab className="layerlist-tab-button">Flyfoto</Tab>
                </TabList>
                <TabPanel className="layerlist-tab-panel" selectedClassName="selected">
                    {/* <div className="layerlist-details-custom">
            <NfsButton
                outlined
                onClick={() => { }}
            >
                <PlusIcon />
                Legg til eget
            </NfsButton>
        </div> */}
                    <div className="layerlist-show-removed-objects">
                        {nfsStatusFilterEnabled && (
                            <div className="layerlist-show-removed-objects-remark">
                                Statusfilteret er aktivt og overstyrer dette valget. Skru av filteret under fanen
                                «Filter» for å bruke det igjen.
                            </div>
                        )}
                        <div className="flex-row small-gap center">
                            <Checkbox
                                disabled={nfsStatusFilterEnabled}
                                id="show-removed-objects"
                                checked={showRemovedFeatures}
                                onChange={(value) => dispatch(setShowRemovedFeatures(value))}
                            />
                            <label htmlFor="show-removed-objects">Vis nedlagte og fjernede objekter</label>
                        </div>
                    </div>
                    <div className="layerlist-details-layers">
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title="Favoritter"
                            titleClassName="layerlist-details-title"
                            className="layerlist-details-collapsible"
                        >
                            {favoriteLayers}
                        </Collapsible>
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title="NFS Kartlag"
                            titleClassName="layerlist-details-title"
                            className="layerlist-details-collapsible"
                        >
                            {nfsLayersList}
                        </Collapsible>
                        {renderLayersFromWebMap()}
                    </div>
                </TabPanel>
                <TabPanel className="layerlist-tab-panel" selectedClassName="selected">
                    <arcgis-layer-list
                        ref={layerListCallbackRef}
                        referenceElement={"#map"}
                        dragEnabled
                        visibilityAppearance="checkbox"
                    ></arcgis-layer-list>
                </TabPanel>
                <TabPanel className="layerlist-tab-panel" selectedClassName="selected">
                    <AerialImagery />
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default LayerlistDetails;
